import api from './api';

/**
 * Werkplek professional: Dashboard-submenu (Actiepunten, Alle signalen,
 * Opdrachten) en Contact-hub. Server: src/routes/professionalWerkplek.ts.
 */

const BASIS = '/v1/professional';

export const VENSTERS = [
  'new_message',
  'connection_request',
  'dossier_update',
  'invoice',
  'vacancy',
  'community',
  'compliance',
  'platform',
] as const;
export type Venster = (typeof VENSTERS)[number];

export type SignalSeverity = 'urgent' | 'actie' | 'info';

export interface WerkplekSignal {
  id: string;
  key: string;
  source: 'afgeleid' | 'notificatie';
  venster: Venster;
  severity: SignalSeverity;
  params: Record<string, string | number | null>;
  title?: string;
  content?: string;
  deeplink: string;
  occurredAt: string;
  isRead: boolean;
}

export interface SignalenResponse {
  signalen: WerkplekSignal[];
  tellers: Record<Venster, { totaal: number; ongelezen: number; actie: number }>;
  ongelezen: number;
  vensters: Venster[];
  voorkeuren: Record<string, boolean>;
}

export interface ActiepuntenResponse {
  actiepunten: WerkplekSignal[];
  urgent: number;
}

export type ComplianceTier = 'GOUD' | 'ZILVER' | 'BRONS' | 'ONGEVERIFIEERD' | 'NON_COMPLIANT_HOLD';

export interface MarktOpdracht {
  id: string;
  employer_id: string;
  titel: string;
  beschrijving: string | null;
  professional_type: string;
  provincie: string | null;
  active_regions: string[] | null;
  work_preference: string | null;
  uren_per_week_min: number | null;
  uren_per_week_max: number | null;
  tarief_vanaf: number | null;
  tarief_tot: number | null;
  hourly_rate_max: number | null;
  sluitingsdatum: string | null;
  published_at: string | null;
  min_compliance_tier: 'GOUD' | 'ZILVER' | 'BRONS' | null;
  company_location: string | null;
  hours_per_month_staffel: string | null;
  experience_level_required: string | null;
  years_experience_min: number | null;
  organisatie: string | null;
  organisatie_logo: string | null;
  application_status: string | null;
  /** Hemelsbreed vanaf het eigen KvK-kantooradres; null bij remote of onbekende locatie. */
  afstand_km: number | null;
}

export interface OpdrachtenResponse {
  opdrachten: MarktOpdracht[];
  /** Toegepast werkgebied in km; null = heel Nederland. */
  straal_km: number | null;
  /** Reisradius uit het profiel; null = heel Nederland. */
  profiel_straal_km: number | null;
  locatie_bekend: boolean;
  tier: ComplianceTier;
  disciplines: string[];
  blokkade: 'hold' | 'geen_discipline' | null;
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface GekoppeldeOpdrachtgever {
  company_id: string;
  organisatie: string;
}

export interface OpdrachtgeverContact extends GekoppeldeOpdrachtgever {
  contactpersoon: string | null;
  telefoon: string | null;
}

export const werkplekApi = {
  actiepunten: async (): Promise<ActiepuntenResponse> => (await api.get(`${BASIS}/dashboard/actiepunten`)).data.data,

  signalen: async (): Promise<SignalenResponse> => (await api.get(`${BASIS}/dashboard/signalen`)).data.data,

  markeerGelezen: async (signalId: string): Promise<void> => {
    await api.post(`${BASIS}/dashboard/signalen/gelezen`, { signal_id: signalId });
  },

  zetVenster: async (venster: Venster, wijziging: { sms?: boolean; email?: boolean }): Promise<Record<string, boolean>> =>
    (await api.put(`${BASIS}/dashboard/notifications/${venster}`, wijziging)).data.data.voorkeuren,

  /** straal: km, 'landelijk', of weglaten voor de reisradius uit het profiel. */
  opdrachten: async (params: { straal?: number | 'landelijk'; search?: string; page?: number }): Promise<OpdrachtenResponse> => {
    const res = await api.get(`${BASIS}/dashboard/available-jobs`, { params });
    return { ...res.data.data, pagination: res.data.pagination };
  },

  opdrachtgevers: async (): Promise<GekoppeldeOpdrachtgever[]> => (await api.get(`${BASIS}/support/employer-contacts`)).data.data,

  opdrachtgeverContact: async (companyId: string): Promise<OpdrachtgeverContact> =>
    (await api.get(`${BASIS}/support/employer-contact/${companyId}`)).data.data,
};
